import type { ProductCardProduct } from "@/components/product/ProductCard";

/* ------------------------------------------------------------------ */
/*  Shared sample product data used across listing & detail pages      */
/* ------------------------------------------------------------------ */

export interface ProductVariantData {
  id: string;
  name: string;
  sku: string | null;
  price: number | null;
  stockCount: number;
  inStock: boolean;
  options: Record<string, string>;
  sortOrder: number;
}

export interface ProductDetail extends ProductCardProduct {
  description: string;
  shortDescription: string;
  images: string[];
  imageAlts?: (string | null)[];
  specs: Record<string, string>;
  colors: { name: string; hex: string }[];
  variants?: ProductVariantData[];
  stockCount?: number;
  // preorderEnabled + preorderShipDate inherited from ProductCardProduct
}

/* ---------- Wigs ---------- */

export const SAMPLE_WIGS: ProductDetail[] = [
  {
    id: "w1",
    name: "The Sophia",
    slug: "the-sophia",
    price: 4200,
    image: "/images/products/wig-1.jpg",
    isNew: true,
    inStock: true,
    stockCount: 3,
    category: "wigs",
    shortDescription:
      "A luxurious European-hair wig with a natural silk-top base and effortless body wave.",
    description:
      "The Sophia is crafted from the finest European hair, hand-tied onto a breathable silk-top cap for a flawlessly natural scalp appearance. The gentle body wave offers versatile styling options, from sleek and polished to soft and voluminous. Every strand is carefully selected and processed to maintain its natural luster and longevity.",
    images: ["/images/products/wig-1.jpg"],
    specs: { Length: '16"', Texture: "Body Wave", "Base Type": "Silk Top", "Cap Size": "Average" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Chestnut", hex: "#6B3A2A" },
      { name: "Honey Blonde", hex: "#B8860B" },
    ],
  },
  {
    id: "w2",
    name: "The Ava",
    slug: "the-ava",
    price: 5600,
    comparePrice: 6200,
    image: "/images/products/wig-2.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription: "Silky straight European hair on a mono-top base for a seamless look.",
    description:
      "The Ava combines the ultimate in sleek sophistication with daily comfort. Made from premium European hair, it features a mono-top base that provides a natural scalp illusion from every angle. The straight texture is easy to maintain and can be styled with heat for added versatility.",
    images: ["/images/products/wig-2.jpg"],
    specs: { Length: '18"', Texture: "Straight", "Base Type": "Mono Top", "Cap Size": "Average" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Medium Brown", hex: "#5C3A1E" },
    ],
  },
  {
    id: "w3",
    name: "The Isabella",
    slug: "the-isabella",
    price: 7200,
    image: "/images/products/wig-3.jpg",
    isNew: true,
    inStock: true,
    stockCount: 1,
    category: "wigs",
    shortDescription: "Our most luxurious piece featuring extra-long, cascading curls.",
    description:
      "The Isabella is the crown jewel of our collection. With 22 inches of cascading European curls, this wig makes a statement. The lace-top base ensures an undetectable hairline, while the adjustable cap fits securely for all-day wear. Perfect for special occasions or everyday glamour.",
    images: ["/images/products/wig-3.jpg"],
    specs: { Length: '22"', Texture: "Curly", "Base Type": "Lace Top", "Cap Size": "Average" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Auburn", hex: "#8B4513" },
    ],
  },
  {
    id: "w4",
    name: "The Charlotte",
    slug: "the-charlotte",
    price: 3800,
    image: "/images/products/wig-4.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription: "A lightweight everyday wig with a natural wavy texture and mono-top base.",
    description:
      "The Charlotte is designed for everyday elegance. Its medium-length wavy texture provides natural movement, while the lightweight mono-top cap keeps you comfortable from morning to night. An excellent choice for women seeking a balance of beauty and practicality.",
    images: ["/images/products/wig-4.jpg"],
    specs: { Length: '14"', Texture: "Wavy", "Base Type": "Mono Top", "Cap Size": "Petite" },
    colors: [
      { name: "Light Brown", hex: "#8B6914" },
      { name: "Dark Blonde", hex: "#B8860B" },
      { name: "Medium Brown", hex: "#5C3A1E" },
    ],
  },
  {
    id: "w5",
    name: "The Olivia",
    slug: "the-olivia",
    price: 6800,
    image: "/images/products/wig-5.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription: "Sleek and straight with a lace-top base for an invisible hairline.",
    description:
      "The Olivia offers a modern, polished look with 20 inches of silky straight European hair. The full lace-top base creates an undetectable hairline and allows for versatile parting. Whether worn down or styled in an updo, The Olivia delivers flawless results.",
    images: ["/images/products/wig-5.jpg"],
    specs: { Length: '20"', Texture: "Straight", "Base Type": "Lace Top", "Cap Size": "Large" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Espresso", hex: "#2B1810" },
    ],
  },
  {
    id: "w6",
    name: "The Amelia",
    slug: "the-amelia",
    price: 2800,
    comparePrice: 3400,
    image: "/images/products/wig-6.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription: "An affordable yet stunning short wig with soft waves and silk-top base.",
    description:
      "The Amelia proves that luxury doesn't have to break the bank. This short and chic wig features soft European waves on a silk-top base, ideal for those who prefer a low-maintenance yet sophisticated style. The petite cap size fits snugly for smaller head circumferences.",
    images: ["/images/products/wig-6.jpg"],
    specs: { Length: '10"', Texture: "Wavy", "Base Type": "Silk Top", "Cap Size": "Petite" },
    colors: [
      { name: "Medium Brown", hex: "#5C3A1E" },
      { name: "Chestnut", hex: "#6B3A2A" },
    ],
  },
  {
    id: "w7",
    name: "The Victoria",
    slug: "the-victoria",
    price: 8500,
    image: "/images/products/wig-7.jpg",
    isNew: false,
    inStock: false,
    category: "wigs",
    shortDescription: "Our signature piece with hand-tied, multi-dimensional highlights.",
    description:
      "The Victoria is a limited masterpiece. Each wig is meticulously hand-tied with premium European hair featuring multi-dimensional highlights for breathtaking depth and movement. The lace-top base and large cap size accommodate fuller hairstyles with effortless grace.",
    images: ["/images/products/wig-7.jpg"],
    specs: { Length: '24"', Texture: "Body Wave", "Base Type": "Lace Top", "Cap Size": "Large" },
    colors: [
      { name: "Highlighted Brown", hex: "#6B4226" },
      { name: "Caramel", hex: "#A0522D" },
    ],
  },
  {
    id: "w8",
    name: "The Grace",
    slug: "the-grace",
    price: 4900,
    image: "/images/products/wig-8.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription: "A timeless medium-length wig with gentle curls and silk-top construction.",
    description:
      "The Grace embodies timeless elegance with its gently curling European hair and premium silk-top base. The medium length makes it versatile for both casual and formal settings, while the average cap size accommodates most head shapes comfortably.",
    images: ["/images/products/wig-8.jpg"],
    specs: { Length: '16"', Texture: "Curly", "Base Type": "Silk Top", "Cap Size": "Average" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Medium Brown", hex: "#5C3A1E" },
      { name: "Light Brown", hex: "#8B6914" },
    ],
  },
  {
    id: "w9",
    name: "The Eleanor",
    slug: "the-eleanor",
    price: 5200,
    image: "/images/products/wig-9.jpg",
    isNew: true,
    inStock: true,
    category: "wigs",
    shortDescription:
      "A sophisticated long straight wig with a mono-top base for all-day comfort.",
    description:
      "The Eleanor is for women who desire length without sacrificing comfort. At 18 inches of straight European hair on a breathable mono-top base, it delivers a polished, professional look that transitions seamlessly from day to evening.",
    images: ["/images/products/wig-9.jpg"],
    specs: { Length: '18"', Texture: "Straight", "Base Type": "Mono Top", "Cap Size": "Average" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Espresso", hex: "#2B1810" },
      { name: "Dark Brown", hex: "#3B2314" },
    ],
  },
  {
    id: "w10",
    name: "The Penelope",
    slug: "the-penelope",
    price: 3200,
    image: "/images/products/wig-10.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription:
      "A short, sassy wig with natural curls, perfect for a carefree lifestyle.",
    description:
      "The Penelope celebrates natural texture with bouncy, defined curls in a short, easy-to-manage length. Built on a silk-top base, it gives the appearance of hair growing directly from the scalp. Ideal for women who love a playful, youthful look.",
    images: ["/images/products/wig-10.jpg"],
    specs: { Length: '12"', Texture: "Curly", "Base Type": "Silk Top", "Cap Size": "Average" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Auburn", hex: "#8B4513" },
      { name: "Chestnut", hex: "#6B3A2A" },
    ],
  },
  {
    id: "w11",
    name: "The Scarlett",
    slug: "the-scarlett",
    price: 6200,
    comparePrice: 7000,
    image: "/images/products/wig-11.jpg",
    isNew: false,
    inStock: true,
    category: "wigs",
    shortDescription:
      "A glamorous long body-wave wig with lace-top base and rich color options.",
    description:
      "The Scarlett turns heads with its long, flowing body waves. Crafted from premium European hair and set on a lace-top base, it offers both a natural hairline and sumptuous volume. The rich color options complement a wide range of skin tones beautifully.",
    images: ["/images/products/wig-11.jpg"],
    specs: { Length: '20"', Texture: "Body Wave", "Base Type": "Lace Top", "Cap Size": "Average" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Burgundy", hex: "#722F37" },
    ],
  },
  {
    id: "w12",
    name: "The Vivienne",
    slug: "the-vivienne",
    price: 2200,
    image: "/images/products/wig-12.jpg",
    isNew: false,
    inStock: false,
    category: "wigs",
    shortDescription: "An entry-level European hair wig with a wavy texture and mono-top base.",
    description:
      "The Vivienne makes European hair accessible without compromising on quality. This affordable wig features a flattering wavy texture on a mono-top base, providing a natural look that's easy to style and maintain. An excellent first wig or everyday alternative.",
    images: ["/images/products/wig-12.jpg"],
    specs: { Length: '14"', Texture: "Wavy", "Base Type": "Mono Top", "Cap Size": "Average" },
    colors: [
      { name: "Medium Brown", hex: "#5C3A1E" },
      { name: "Dark Blonde", hex: "#B8860B" },
    ],
  },
];

/* ---------- Toppers ---------- */

export const SAMPLE_TOPPERS: ProductDetail[] = [
  {
    id: "t1",
    name: "The Crown Piece",
    slug: "the-crown-piece",
    price: 2400,
    image: "/images/products/topper-1.jpg",
    isNew: true,
    inStock: true,
    category: "toppers",
    shortDescription:
      "A premium silk-top topper that blends seamlessly with your natural hair.",
    description:
      "The Crown Piece is designed for women experiencing thinning at the crown. Crafted from European hair on a silk-top base, it clips securely into your existing hair and creates a natural, voluminous look. The 14-inch length allows for versatile styling.",
    images: ["/images/products/topper-1.jpg"],
    specs: { Length: '14"', Texture: "Straight", "Base Type": "Silk Top", Coverage: "Crown" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Medium Brown", hex: "#5C3A1E" },
      { name: "Light Brown", hex: "#8B6914" },
    ],
  },
  {
    id: "t2",
    name: "The Volume Boost",
    slug: "the-volume-boost",
    price: 1800,
    comparePrice: 2200,
    image: "/images/products/topper-2.jpg",
    isNew: false,
    inStock: true,
    category: "toppers",
    shortDescription:
      "Add instant volume and coverage with this lightweight clip-in topper.",
    description:
      "The Volume Boost is perfect for adding fullness to thinning hair. This lightweight topper features a breathable mono base with European hair that blends naturally with your own. Pressure-sensitive clips ensure a secure, comfortable hold throughout the day.",
    images: ["/images/products/topper-2.jpg"],
    specs: { Length: '12"', Texture: "Wavy", "Base Type": "Mono Top", Coverage: "Crown & Part" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Chestnut", hex: "#6B3A2A" },
    ],
  },
  {
    id: "t3",
    name: "The Parting Piece",
    slug: "the-parting-piece",
    price: 3200,
    image: "/images/products/topper-3.jpg",
    isNew: true,
    inStock: true,
    category: "toppers",
    shortDescription:
      "A lace-top topper with a natural part line for front-of-head coverage.",
    description:
      "The Parting Piece features an extended lace-top base that creates an incredibly realistic part line. Ideal for women with thinning along the part or hairline, it provides generous coverage while remaining virtually undetectable.",
    images: ["/images/products/topper-3.jpg"],
    specs: { Length: '16"', Texture: "Straight", "Base Type": "Lace Top", Coverage: "Part & Crown" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Medium Brown", hex: "#5C3A1E" },
    ],
  },
  {
    id: "t4",
    name: "The Full Coverage",
    slug: "the-full-coverage",
    price: 3500,
    image: "/images/products/topper-4.jpg",
    isNew: false,
    inStock: true,
    category: "toppers",
    shortDescription:
      "Maximum coverage topper for significant hair loss with a large base.",
    description:
      "The Full Coverage topper provides the most generous coverage in our collection. With a 7x8 inch base and 18 inches of premium European hair, it's designed for women experiencing more advanced thinning who want a natural, full look without committing to a full wig.",
    images: ["/images/products/topper-4.jpg"],
    specs: { Length: '18"', Texture: "Body Wave", "Base Type": "Silk Top", Coverage: "Full Top" },
    colors: [
      { name: "Jet Black", hex: "#0C0C0C" },
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Auburn", hex: "#8B4513" },
    ],
  },
  {
    id: "t5",
    name: "The Bangs Topper",
    slug: "the-bangs-topper",
    price: 1200,
    image: "/images/products/topper-5.jpg",
    isNew: false,
    inStock: true,
    category: "toppers",
    shortDescription: "A clip-in bang piece for instant fringe without commitment.",
    description:
      "The Bangs Topper is a quick, easy way to add bangs to any hairstyle. Made from premium European hair, it clips in at the hairline and provides natural-looking fringe coverage. Perfect for trying a new look or concealing a receding hairline.",
    images: ["/images/products/topper-5.jpg"],
    specs: { Length: '10"', Texture: "Straight", "Base Type": "Lace Top", Coverage: "Fringe" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Medium Brown", hex: "#5C3A1E" },
      { name: "Dark Blonde", hex: "#B8860B" },
    ],
  },
  {
    id: "t6",
    name: "The Curly Crown",
    slug: "the-curly-crown",
    price: 2800,
    image: "/images/products/topper-6.jpg",
    isNew: false,
    inStock: false,
    category: "toppers",
    shortDescription: "A curly topper that adds texture and volume to straight or thin hair.",
    description:
      "The Curly Crown brings beautiful, bouncy curls to the crown area. Crafted from European hair on a silk-top base, it blends beautifully with naturally curly hair or adds texture to straight styles. The curls are set permanently for lasting shape.",
    images: ["/images/products/topper-6.jpg"],
    specs: { Length: '14"', Texture: "Curly", "Base Type": "Silk Top", Coverage: "Crown" },
    colors: [
      { name: "Dark Brown", hex: "#3B2314" },
      { name: "Chestnut", hex: "#6B3A2A" },
    ],
  },
];

/* ---------- Accessories (salon supplies + drop-ship) ---------- */

export const SAMPLE_ACCESSORIES: ProductDetail[] = [
  {
    id: "a1",
    name: "Luxury Wig Stand",
    slug: "luxury-wig-stand",
    price: 45,
    image: "/images/products/accessory-1.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "A premium collapsible wig stand to maintain your wig's shape.",
    description:
      "Keep your wig looking its best with our luxury collapsible wig stand. Made from lightweight yet sturdy materials, it maintains your wig's shape, allows air circulation, and folds flat for easy travel. Features a non-slip base and adjustable height.",
    images: ["/images/products/accessory-1.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a2",
    name: "Detangling Brush Set",
    slug: "detangling-brush-set",
    price: 32,
    image: "/images/products/accessory-2.jpg",
    isNew: true,
    inStock: true,
    category: "accessories",
    shortDescription: "A three-piece brush set designed specifically for wig care.",
    description:
      "This three-piece detangling brush set is engineered specifically for wig care. Includes a wide-tooth comb for wet detangling, a loop brush for gentle daily styling, and a fine-tooth comb for finishing touches. The flexible bristles prevent snagging and minimize shedding.",
    images: ["/images/products/accessory-2.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a3",
    name: "Wig Cap Liner (3-Pack)",
    slug: "wig-cap-liner-3-pack",
    price: 18,
    image: "/images/products/accessory-3.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "Breathable, moisture-wicking cap liners for all-day comfort.",
    description:
      "Our premium wig cap liners are made from a breathable bamboo-blend fabric that wicks moisture and reduces friction between your scalp and wig. The seamless construction eliminates pressure points. Available in a convenient 3-pack.",
    images: ["/images/products/accessory-3.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a4",
    name: "Lace Adhesive",
    slug: "lace-adhesive",
    price: 24,
    image: "/images/products/accessory-4.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "Medical-grade, waterproof lace adhesive for secure all-day hold.",
    description:
      "Our medical-grade lace adhesive provides a secure, waterproof hold for lace-front wigs and toppers. Gentle on skin, easy to apply, and removes cleanly with our adhesive remover. One bottle provides approximately 30 applications.",
    images: ["/images/products/accessory-4.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a5",
    name: "Travel Case",
    slug: "travel-case",
    price: 89,
    image: "/images/products/accessory-5.jpg",
    isNew: true,
    inStock: true,
    category: "accessories",
    shortDescription: "A protective hard-shell travel case for your wig.",
    description:
      "Travel with confidence using our protective hard-shell wig travel case. Lined with soft satin to prevent tangling and frizz, it features a built-in wig stand, accessory pocket, and TSA-friendly lock. Sized to fit wigs up to 24 inches.",
    images: ["/images/products/accessory-5.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a6",
    name: "Wig Shampoo & Conditioner",
    slug: "wig-shampoo-conditioner",
    price: 38,
    image: "/images/products/accessory-6.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "Sulfate-free shampoo and conditioner formulated for human-hair wigs.",
    description:
      "Our sulfate-free shampoo and conditioner duo is specially formulated for European human-hair wigs. The gentle, pH-balanced formula cleanses without stripping natural oils, while the deep conditioner restores moisture and shine. Extends the life of your wig with every wash.",
    images: ["/images/products/accessory-6.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a7",
    name: "Adhesive Remover",
    slug: "adhesive-remover",
    price: 15,
    image: "/images/products/accessory-7.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "Gentle adhesive remover that dissolves bonding agents without residue.",
    description:
      "Our gentle adhesive remover quickly dissolves wig bonding agents without leaving residue on your skin or lace. Enriched with aloe vera and vitamin E to soothe the skin during removal. Works with all major adhesive brands.",
    images: ["/images/products/accessory-7.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "a8",
    name: "Silk Wig Storage Bag",
    slug: "silk-wig-storage-bag",
    price: 120,
    image: "/images/products/accessory-8.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "A premium silk storage bag to protect your wig at home or on the go.",
    description:
      "Protect your investment with our premium silk wig storage bag. Made from 100% mulberry silk, it prevents tangling, reduces static, and preserves your wig's moisture balance. Features a drawstring closure and embroidered logo. Available in blush pink.",
    images: ["/images/products/accessory-8.jpg"],
    specs: {},
    colors: [],
  },
  {
    id: "ds1",
    name: "Satin Ribbon Headband",
    slug: "satin-ribbon-headband",
    price: 28,
    image: "/images/products/accessory-ds-1.jpg",
    isNew: true,
    inStock: true,
    category: "accessories",
    shortDescription: "A luxurious satin ribbon headband that keeps your wig secure while adding a stylish accent.",
    description:
      "A luxurious satin ribbon headband that keeps your wig secure while adding a stylish accent. Adjustable elastic for a comfortable fit that works with any wig or topper style. Available in classic colors to match every outfit.",
    images: ["/images/products/accessory-ds-1.jpg"],
    specs: {},
    colors: [
      { name: "Black", hex: "#0C0C0C" },
      { name: "Navy", hex: "#1B1F3B" },
      { name: "Blush", hex: "#DE98A0" },
    ],
  },
  {
    id: "ds2",
    name: "Crystal Hair Clips (Set of 6)",
    slug: "crystal-hair-clips-set",
    price: 35,
    comparePrice: 42,
    image: "/images/products/accessory-ds-2.jpg",
    isNew: true,
    inStock: true,
    category: "accessories",
    shortDescription: "Elegant crystal-embellished hair clips that add sparkle to any wig style.",
    description:
      "Elegant crystal-embellished hair clips that add sparkle to any wig style. Set of 6 assorted sizes for versatile styling. The smooth clip mechanism is gentle on wig fibers and won't cause snagging or damage.",
    images: ["/images/products/accessory-ds-2.jpg"],
    specs: {},
    colors: [
      { name: "Silver", hex: "#C0C0C0" },
      { name: "Gold", hex: "#D4AF37" },
      { name: "Rose Gold", hex: "#B76E79" },
    ],
  },
  {
    id: "ds3",
    name: "Velvet Wig Grip Band",
    slug: "velvet-wig-grip-band",
    price: 22,
    image: "/images/products/accessory-ds-3.jpg",
    isNew: false,
    inStock: true,
    category: "accessories",
    shortDescription: "Non-slip velvet wig grip band that holds your wig in place without pins or adhesive.",
    description:
      "Non-slip velvet wig grip band that holds your wig in place without pins or adhesive. Comfortable for all-day wear with a breathable design that won't irritate your scalp. Machine washable and built to last.",
    images: ["/images/products/accessory-ds-3.jpg"],
    specs: {},
    colors: [
      { name: "Black", hex: "#0C0C0C" },
      { name: "Brown", hex: "#3B2314" },
      { name: "Beige", hex: "#D2B48C" },
    ],
  },
  {
    id: "ds4",
    name: "Lace-Front Headband Insert",
    slug: "lace-front-headband-insert",
    price: 42,
    image: "/images/products/accessory-ds-4.jpg",
    isNew: true,
    inStock: true,
    category: "accessories",
    shortDescription: "A specially designed headband insert that creates a seamless transition with lace-front wigs.",
    description:
      "A specially designed headband insert that creates a seamless transition with lace-front wigs. Features adjustable tension for the perfect fit. The thin profile sits flush against your forehead for an invisible, natural look.",
    images: ["/images/products/accessory-ds-4.jpg"],
    specs: {},
    colors: [
      { name: "Black", hex: "#0C0C0C" },
      { name: "Brown", hex: "#3B2314" },
    ],
  },
];

/* ---------- Combined for search ---------- */

export const ALL_PRODUCTS: ProductDetail[] = [
  ...SAMPLE_WIGS,
  ...SAMPLE_TOPPERS,
  ...SAMPLE_ACCESSORIES,
];
